import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { recordingService } from '../services/api';

const RecordingDetail = () => {
  const { recordingId } = useParams();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [recording, setRecording] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [transcriptExpanded, setTranscriptExpanded] = useState(false);
  const [isEditingTranscript, setIsEditingTranscript] = useState(false);
  const [editedTranscript, setEditedTranscript] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchRecording = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await recordingService.getRecordingDetails(recordingId);
        setRecording(response);
        setEditedTranscript(response.message_content || '');
      } catch (error) {
        console.error('Error fetching recording details:', error);
        setError('Failed to load recording details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchRecording();
  }, [recordingId]);

  const handleEditTranscript = () => {
    setIsEditingTranscript(true);
    setEditedTranscript(recording.message_content || '');
  };

  const handleSaveTranscript = async () => {
    try {
      setSaving(true);
      const updatedRecording = await recordingService.updateRecordingTranscript(recordingId, {
        message_content: editedTranscript
      });
      setRecording(updatedRecording);
      setIsEditingTranscript(false);
    } catch (error) {
      console.error('Error saving transcript:', error);
      setError('Failed to save transcript. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingTranscript(false);
    setEditedTranscript(recording.message_content || '');
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString();
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString();
  };

  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';
    // Remove any non-digit characters except +
    const cleaned = phoneNumber.replace(/[^\d+]/g, '');
    return cleaned;
  };

  const getWhatsAppLink = (phoneNumber) => {
    const formatted = formatPhoneNumber(phoneNumber);
    return `https://wa.me/${formatted.startsWith('+') ? formatted.slice(1) : formatted}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'processed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'skipped':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'processed':
        return t('recordings.status.processed', 'Processed');
      case 'pending':
        return t('recordings.status.pending', 'Pending');
      case 'skipped':
        return t('recordings.status.skipped', 'Skipped');
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center my-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (error || !recording) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 text-lg">
          {error || t('recordings.detail.notFound', 'Recording not found')}
        </p>
        <Link to="/recordings" className="text-blue-500 hover:underline mt-4 inline-block">
          {t('recordings.detail.backToRecordings', 'Back to Recordings')}
        </Link>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6">
        <Link to="/recordings" className="text-blue-500 hover:underline flex items-center">
          ← {t('recordings.detail.backToRecordings', 'Back to Recordings')}
        </Link>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-start mb-6">
          <h1 className="text-2xl font-bold">
            {t('recordings.detail.title', 'Phone Call Recording')}
          </h1>
          <span className={`px-3 py-1 rounded-full text-sm ${getStatusColor(recording.status)}`}>
            {getStatusText(recording.status)}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="space-y-3">
            <div>
              <span className="font-semibold text-gray-700">
                {t('recordings.detail.caller', 'Caller')}:
              </span>
              <div className="mt-1">
                <a
                  href={getWhatsAppLink(recording.from_number)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-green-600 hover:text-green-800 hover:underline"
                >
                  {recording.from_number}
                </a>
                {recording.profile_name && (
                  <div className="text-sm text-gray-500">({recording.profile_name})</div>
                )}
              </div>
            </div>
            <div>
              <span className="font-semibold text-gray-700">
                {t('recordings.detail.recipient', 'Recipient')}:
              </span>
              <a
                href={getWhatsAppLink(recording.to_number)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-green-600 hover:text-green-800 hover:underline ml-2"
              >
                {recording.to_number}
              </a>
            </div>
            <div>
              <span className="font-semibold text-gray-700">
                {t('recordings.detail.date', 'Date')}:
              </span>
              <span className="text-gray-900 ml-2">{formatDate(recording.timestamp)}</span>
            </div>
            <div>
              <span className="font-semibold text-gray-700">
                {t('recordings.detail.time', 'Time')}:
              </span>
              <span className="text-gray-900 ml-2">{formatTime(recording.timestamp)}</span>
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <span className="font-semibold text-gray-700">
                {t('recordings.detail.duration', 'Duration')}:
              </span>
              <span className="text-gray-900 ml-2">
                {recording.duration || t('recordings.detail.noDuration', 'Unknown')}
              </span>
            </div>
            <div>
              <span className="font-semibold text-gray-700">
                {t('recordings.detail.conversationId', 'Conversation ID')}:
              </span>
              <span className="text-gray-900 ml-2 font-mono text-sm">{recording.conversation_id}</span>
            </div>
            <div>
              <span className="font-semibold text-gray-700">
                {t('recordings.detail.source', 'Source')}:
              </span>
              <span className="text-gray-900 ml-2">{recording.source || 'Unknown'}</span>
            </div>
          </div>
        </div>

        {recording.media_url && (
          <div className="mb-6">
            <h3 className="font-semibold mb-3">
              {t('recordings.detail.audioRecording', 'Audio Recording')}:
            </h3>
            <div className="bg-gray-100 p-4 rounded-md">
              <audio controls className="w-full">
                <source src={recording.media_url} type="audio/mpeg" />
                {t('recordings.detail.audioNotSupported', 'Your browser does not support the audio element.')}
              </audio>
              <div className="mt-2 text-sm text-gray-600">
                <a
                  href={recording.media_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800"
                >
                  {t('recordings.detail.downloadAudio', 'Download Audio File')}
                </a>
              </div>
            </div>
          </div>
        )}

        <div>
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-semibold">
              {t('recordings.detail.transcript', 'Transcript')}:
            </h3>
            <div className="flex space-x-2">
              {!isEditingTranscript ? (
                <>
                  <button
                    onClick={handleEditTranscript}
                    className="text-blue-500 hover:text-blue-700 text-sm"
                  >
                    {t('recordings.detail.editTranscript', 'Edit')}
                  </button>
                  <button
                    onClick={() => setTranscriptExpanded(!transcriptExpanded)}
                    className="text-blue-500 hover:text-blue-700 text-sm"
                  >
                    {transcriptExpanded
                      ? t('recordings.detail.collapse', 'Collapse')
                      : t('recordings.detail.expand', 'Expand')
                    }
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={handleSaveTranscript}
                    disabled={saving}
                    className="px-3 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600 disabled:opacity-50"
                  >
                    {saving ? t('recordings.detail.saving', 'Saving...') : t('recordings.detail.save', 'Save')}
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="px-3 py-1 bg-gray-500 text-white rounded text-sm hover:bg-gray-600"
                  >
                    {t('recordings.detail.cancel', 'Cancel')}
                  </button>
                </>
              )}
            </div>
          </div>

          {isEditingTranscript ? (
            <div className="bg-gray-100 p-4 rounded-md">
              <textarea
                value={editedTranscript}
                onChange={(e) => setEditedTranscript(e.target.value)}
                className="w-full h-64 p-3 border border-gray-300 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={t('recordings.detail.transcriptPlaceholder', 'Enter transcript here...')}
              />
            </div>
          ) : (
            <div className={`bg-gray-100 p-4 rounded-md ${transcriptExpanded ? '' : 'max-h-60 overflow-y-auto'}`}>
              <pre className="whitespace-pre-wrap font-sans text-sm">
                {recording.message_content || t('recordings.detail.noTranscript', 'No transcript available')}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecordingDetail;
